import React, {
  useEffect, useState, useRef, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { connect } from 'react-redux';
import $ from 'jquery';

import Master from '../Master';
import Select from '../Select';
import TextInput from '../TextInput';
import IziToast from '../IziToast';
import Button from '../Buttons';
import ChargesTable from './ChargesTable';
import { FormatDate, DestroyTable, SortByLatest } from '../../utils/Functions';
import CheckBox from '../CheckBox';

const ExtraCharge = ({ user }) => {
  // token
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };

  //   refs
  const chargeRef = useRef(null);
  const amountRef = useRef(null);
  const tenantRef = useRef(null);
  const ownerRef = useRef(null);
  const propertyRef = useRef(null);

  // state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [chargeAmount, setChargeAmount] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [units, setUnits] = useState([]);
  const [extraCharges, setExtraCharges] = useState([]);
  const [extraChargesList, setExtraChargesList] = useState([]);
  const [chargeId, setChargeId] = useState('');
  const [description, setDescription] = useState('');
  const [hasError, setHasError] = useState(false);
  const [propertyId, setPropertyId] = useState('');
  const [tenantsList, setTenantsList] = useState([]);
  const [chargeList, setChargeList] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  // Charge recipient — 'tenant' (default) or 'unit_owner'.
  const [recipientType, setRecipientType] = useState('tenant');
  const [unitOwners, setUnitOwners] = useState([]);
  const [unitOwnersList, setUnitOwnersList] = useState([]);
  const [unitOwnerId, setUnitOwnerId] = useState('');

  // ── List filters (independent of the form) ─────────────────────────────
  // The charges list browses the FULL history, not just the unpaid rows,
  // so the operator can audit settled / cancelled invoices without
  // round-tripping through Reports. These filters drive the visible
  // subset via a useMemo — changing a filter is instant, no re-fetch.
  const [listFilterProperty, setListFilterProperty] = useState('');
  const [listFilterStatus, setListFilterStatus] = useState('');
  const [listFilterRecipient, setListFilterRecipient] = useState('all');
  const [listFilterSearch, setListFilterSearch] = useState('');

  useEffect(() => {
    DestroyTable('charges_table');
    // The form's property selector narrows the Tenant / Unit Owner
    // dropdowns. It NO LONGER filters the charges list — the list now
    // has its own independent property filter so an operator can be
    // creating a charge for property A while browsing history for
    // property B.
    if (propertyId) {
      const filtered = tenantsList.filter((item) => {
        return item.unit && item.unit.property_id === propertyId;
      });
      setTenants(filtered);
      const filteredOwners = unitOwnersList.filter(
        (item) => item.property_id === propertyId,
      );
      setUnitOwners(filteredOwners);
    } else {
      setTenants(tenantsList);
      setUnitOwners(unitOwnersList);
    }
    setTenantId('');
    setUnitOwnerId('');
    // Wipe select2's visible display too. Use the namespaced
    // `change.select2` event so we trigger select2's UI sync WITHOUT
    // re-firing our own `change` handler (which would feedback-loop).
    if (tenantRef.current) $(tenantRef.current).val('').trigger('change.select2');
    if (ownerRef.current) $(ownerRef.current).val('').trigger('change.select2');
  }, [propertyId]);

  useEffect(() => {
    const propertyUrl = user && user.type === 'admin'
      ? `${process.env.REACT_APP_API_URL}properties?take=1000&where={"status":"active"}&order={"name":"ASC"}`
      : `${process.env.REACT_APP_API_URL}properties/byuser?status=active&user_id=${user.id}`;

    (async () => {
      try {
        // ── Step 1: properties (needed to filter everything else) ────────
        const { data: { response: property } } = await axios.get(propertyUrl, requestConfig);
        setProperties(property);
        $(propertyRef.current).select2().on('change', (event) => setPropertyId(event.val));

        const propertyIds = property.map((p) => p.id);

        // ── Step 2: units + paymentcodes + invoices in PARALLEL ──────────
        // (units, paymentcodes and invoices are independent of each other —
        //  all they need is the property list which we already have.)
        const unitsUrl = user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}units?take=10000`
          : `${process.env.REACT_APP_API_URL}units?take=10000&where={"property_id":"in::${propertyIds.join()}"}`;

        const codesUrl = user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}paymentcodes?take=1000&order={"createdat":"DESC"}`
          : `${process.env.REACT_APP_API_URL}paymentcodes?take=1000&where={"property_id":"in::${propertyIds.join()}"}&order={"createdat":"DESC"}`;

        // Pull ALL charge invoices (every status). The list is the audit
        // trail — operators need to see paid / partially_paid / cancelled
        // rows too, not just open ones. Filters live in the UI below.
        const invoiceFilter = user.type === 'admin'
          ? '"invoice_type":"charges"'
          : `"invoice_type":"charges","payee_id":"in::${propertyIds.join()}"`;
        const invoicesUrl = `${process.env.REACT_APP_API_URL}invoices?where={${invoiceFilter}}&take=10000&order={"createdat":"DESC"}&include_payment_info=true`;

        const [unitsRes, codesRes, invoicesRes] = await Promise.all([
          axios.get(unitsUrl, requestConfig),
          axios.get(codesUrl, requestConfig),
          axios.get(invoicesUrl, requestConfig),
        ]);
        const unitsInfo = unitsRes.data.response || [];
        const exCharges = codesRes.data.response || [];
        const invoicesInfo = invoicesRes.data.response || [];
        setUnits(unitsInfo);

        // ── Step 3: tenants + unit owners (needs unit_ids for landlords) ──
        const unitIds = unitsInfo.map((u) => u.id);
        const tenantsUrl = user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}tenants?take=10000`
          : `${process.env.REACT_APP_API_URL}tenants?take=10000&where={"unit_id":"in::${unitIds.join()}"}`;
        const ownersUrl = user.type === 'admin'
          ? `${process.env.REACT_APP_API_URL}unitowners?take=10000`
          : `${process.env.REACT_APP_API_URL}unitowners?take=10000&where={"property_id":"in::${propertyIds.join()}"}`;
        const [tenantsRes, ownersRes] = await Promise.all([
          axios.get(tenantsUrl, requestConfig),
          axios.get(ownersUrl, requestConfig).catch(() => ({ data: { response: [] } })),
        ]);
        const tenantsInfo = tenantsRes.data.response || [];
        const ownersInfo = ownersRes.data.response || [];

        // Join units onto tenants for display
        const unitById = new Map(unitsInfo.map((u) => [u.id, u]));
        const mappedTenants = (tenantsInfo || []).map((t) => ({
          ...t,
          unit: unitById.get(t.unit_id),
        }));
        setTenants(mappedTenants);
        setTenantsList(mappedTenants);
        $(tenantRef.current).select2().on('change', (event) => {
          const val = (event && event.target && event.target.value)
            || (event && event.val)
            || '';
          setTenantId(val);
        });

        // Unit owners come pre-joined with `user` and `property` from the
        // manager — no extra mapping needed for the dropdown. The owner
        // <select> is always rendered (just hidden when not active), so
        // the ref is always available here.
        setUnitOwners(ownersInfo);
        setUnitOwnersList(ownersInfo);
        if (ownerRef.current) {
          $(ownerRef.current).select2().on('change', (event) => {
            // Use the native value as the source of truth — select2's
            // `event.val` is unreliable across versions; `event.target.value`
            // is always set when the underlying select changes.
            const val = (event && event.target && event.target.value)
              || (event && event.val)
              || '';
            setUnitOwnerId(val);
          });
        }

        // Map payment codes (kept the legacy unit-attach for downstream parity)
        const mappedCodes = exCharges.map((c) => ({ ...c, unit: unitById.get(c.unit_id) }));
        setExtraCharges(mappedCodes);
        setExtraChargesList(mappedCodes);
        $(chargeRef.current).select2().on('change', (event) => setChargeId(event.val));

        // Join tenants + owners + properties onto invoices for the table.
        // Unit-owner-paid invoices use payer_id = user_id, so we also index
        // owners by their user_id so the table can render the right name.
        const propById = new Map(property.map((p) => [p.id, p]));
        const tenantById = new Map((tenantsInfo || []).map((t) => [t.id, t]));
        const ownerByUserId = new Map(
          (ownersInfo || []).filter((o) => o && o.user_id).map((o) => [o.user_id, o]),
        );
        // Water bills are stored as invoice_type='charges' with
        // payment_code 'WATER'. They share the 'charges' type with genuine
        // extra charges but have their own Finance → Water Bill page, so
        // we exclude them here to keep this list to actual extra charges.
        const mappedInvoices = SortByLatest(invoicesInfo
          .filter((inv) => !(inv.reference_data
            && inv.reference_data.payment_code === 'WATER'))
          .map((inv) => ({
            ...inv,
            tenant: tenantById.get(inv.payer_id),
            owner: inv.payer_type !== 'tenant' ? ownerByUserId.get(inv.payer_id) : null,
            property: propById.get(inv.payee_id),
          })));
        setChargeList(mappedInvoices);
        setIsLoading(false);
      } catch (error) {
        setHasError(true);
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (tenantId) {
      const tenant = tenants.find((item) => {
        return item.id === tenantId;
      });
      const unit = tenant ? units.find((item) => item.id === tenant.unit_id) : null;
      if (unit) {
        const propId = unit.property_id;
        const charge = extraChargesList.filter((item) => item.property_id === propId);
        setExtraCharges(charge);
      } else {
        setExtraCharges(extraChargesList);
      }
    } else {
      setExtraCharges(extraChargesList);
    }
  }, [tenantId]);

  // When a unit owner is selected, narrow the extra-charge dropdown to that
  // owner's property — mirrors the tenant-side effect above.
  useEffect(() => {
    if (unitOwnerId) {
      const owner = unitOwners.find((item) => item.id === unitOwnerId);
      if (owner && owner.property_id) {
        const charge = extraChargesList.filter(
          (item) => item.property_id === owner.property_id,
        );
        setExtraCharges(charge);
      } else {
        setExtraCharges(extraChargesList);
      }
    }
  }, [unitOwnerId]);

  // Clear the inactive recipient when switching mode so we don't carry a
  // stale selection across types. Both dropdowns are ALWAYS rendered (just
  // hidden via CSS), so select2 stays bound across toggles and we don't
  // need to re-init here.
  useEffect(() => {
    if (recipientType === 'tenant') {
      setUnitOwnerId('');
    } else {
      setTenantId('');
    }
  }, [recipientType]);

  /**
   *
   * @param {[]} propertyInfo
   * @param {[]} tenantsInfo
   * @param {[]} ownersInfo
   */
  async function loadCharges(propertyInfo, tenantsInfo, ownersInfo) {
    setIsLoading(true);
    try {
      let filter;
      if (user.type === 'admin') {
        filter = '"invoice_type":"charges"';
      } else {
        const propertyIds = propertyInfo.map((item) => {
          return item.id;
        });
        filter = `"invoice_type":"charges","payee_id":"in::${propertyIds.join()}"`;
      }
      const urlInvoices = `${process.env.REACT_APP_API_URL}invoices?where={${filter}}&take=10000&order={"createdat":"DESC"}&include_payment_info=true`;
      const {
        data: { response },
      } = await axios.get(urlInvoices, requestConfig);
      if (response) {
        // Exclude water bills — they're invoice_type='charges' coded
        // WATER but belong on the Water Bill page, not here.
        const mapped = SortByLatest(response
          .filter((elem) => !(elem.reference_data
            && elem.reference_data.payment_code === 'WATER'))
          .map((elem) => {
            const element = elem;
            element.tenant = findTenant(element.payer_id);
            element.owner = element.payer_type !== 'tenant' ? findOwner(element.payer_id) : null;
            element.property = findProperty(element.payee_id);
            return element;
          }));
        groupCharges(mapped);
        setIsLoading(false);
      }
    } catch (error) {
      IziToast('An error occurred');
      setHasError(true);
      setIsLoading(false);
    }
    function findProperty(id) {
      return propertyInfo.find((property) => {
        return property.id === id;
      });
    }

    function findTenant(id) {
      return tenantsInfo.find((item) => {
        return item.id === id;
      });
    }

    function findOwner(userId) {
      return (ownersInfo || []).find((item) => item && item.user_id === userId);
    }
  }

  function groupCharges(records) {
    setChargeList(records);
  }

  const saveCharges = async () => {
    setIsSubmitting(true);
    // Resolve the recipient (tenant or unit owner) into a uniform
    // {payerId, payerType, payeeId, displayName} shape so the rest of the
    // function only deals with one code path.
    let payerId = '';
    const payerType = recipientType;
    let payeeId = '';
    let displayName = '';
    if (recipientType === 'tenant') {
      if (!tenantId) {
        IziToast('Select tenant details');
        tenantRef.current.focus();
        setIsSubmitting(false);
        return;
      }
      const tenant = tenants.find((item) => item.id === tenantId);
      if (!tenant) {
        IziToast('Selected tenant not found');
        setIsSubmitting(false);
        return;
      }
      const unit = units.find((item) => item.id === tenant.unit_id);
      if (!unit) {
        IziToast('Tenant unit info missing');
        setIsSubmitting(false);
        return;
      }
      payerId = tenantId;
      payeeId = unit.property_id;
      displayName = `${tenant.first_name} ${tenant.last_name}`;
    } else {
      if (!unitOwnerId) {
        IziToast('Select unit owner details');
        if (ownerRef.current) ownerRef.current.focus();
        setIsSubmitting(false);
        return;
      }
      const owner = unitOwners.find((item) => item.id === unitOwnerId);
      if (!owner || !owner.user_id) {
        IziToast('Selected unit owner not found');
        setIsSubmitting(false);
        return;
      }
      payerId = owner.user_id;
      payeeId = owner.property_id;
      displayName = owner.user
        ? `${owner.user.first_name || ''} ${owner.user.last_name || ''}`.trim()
            || owner.user.email
        : 'Unit owner';
    }
    if (!chargeId) {
      IziToast('Select extra charge details');
      chargeRef.current.focus();
      setIsSubmitting(false);
      return;
    }
    const amountNum = parseFloat(chargeAmount);
    if (!chargeAmount || Number.isNaN(amountNum) || amountNum <= 0) {
      IziToast('Enter a valid extra charge amount');
      amountRef.current.focus();
      setIsSubmitting(false);
      return;
    }
    const chargeName = extraCharges.find((item) => item.id === chargeId);

    // Prevent creating a duplicate UNPAID invoice for the same payer +
    // payment_code. `chargeList` now holds invoices of every status, so
    // we narrow to `status: 'unpaid'` explicitly — a previously-PAID
    // charge of the same kind shouldn't block a fresh one.
    const duplicate = chargeList.find((inv) => (
      inv.payer_id === payerId
      && inv.status === 'unpaid'
      && inv.reference_data
      && inv.reference_data.payment_code === chargeName.code
    ));
    if (duplicate) {
      IziToast(
        `${displayName} already has an unpaid ${chargeName.name} invoice. `
        + 'Mark it paid or void it first.',
      );
      setIsSubmitting(false);
      return;
    }
    const changeLog = [
      {
        user: {
          id: user.id,
          type: user.type,
        },
        action: 'Creating invoice',
        timestamp: Math.round(new Date().getTime() / 1000.0),
      },
    ];
    const referenceData = {
      info: chargeName.name || '',
      deduct: false,
      description,
      payment_code: chargeName.code,
    };
    // Carry the charge definition's reconciliation category (if tagged) so
    // payments route correctly (e.g. a "Water Arrears" charge is settled by
    // water payments). Omitted when unspecified → unchanged legacy behaviour.
    if (chargeName.particular) referenceData.particular = chargeName.particular;
    const chargeData = [
      {
        data: { amount: amountNum },
        type: chargeName.name || '',
      },
    ];
    const chargesObject = {
      payer_id: payerId,
      payee_id: payeeId,
      payer_type: payerType,
      payee_type: 'property',
      invoice_type: 'charges',
      invoice_date: FormatDate(new Date()),
      total_amount: amountNum,
      amount_due: amountNum,
      balance: amountNum,
      status: 'unpaid',
      include_charges: false,
      interval: 'month',
      send_email: sendEmail,
      reference_data: referenceData,
      changelog: changeLog,
      send_sms: sendSms,
      send_whatsapp: sendWhatsapp,
      charges: chargeData,
    };
    IziToast('Saving...');
    try {
      const {
        data: { response },
      } = await axios.post(
        `${process.env.REACT_APP_API_URL}invoices`,
        chargesObject,
        requestConfig,
      );
      if (response.id) {
        IziToast('Extra charge successfully saved');
        resetFields();
      }
      onReload();
    } catch (error) {
      const msg = error?.response?.data?.error || error?.message || 'An error occurred';
      IziToast(msg);
    }
    setIsSubmitting(false);
  };

  const resetFields = () => {
    setTenantId('');
    setUnitOwnerId('');
    setChargeId('');
    setChargeAmount('');
    setIsSubmitting(false);
    setDescription('');
  };

  function onReload() {
    loadCharges(properties, tenantsList, unitOwnersList);
  }

  // Owner display label — first/last + email fallback + flattened unit list.
  const ownerLabel = (owner) => {
    if (!owner) return '';
    const name = owner.user
      ? `${owner.user.first_name || ''} ${owner.user.last_name || ''}`.trim()
        || owner.user.email
      : 'Owner';
    const unitList = Array.isArray(owner.unit_numbers) && owner.unit_numbers.length > 0
      ? owner.unit_numbers
        .map((u) => (typeof u === 'string' ? u : (u && u.number) || ''))
        .filter(Boolean)
        .join(', ')
      : '--';
    return `${name} | ${unitList}`;
  };

  // ── Visible charges = chargeList filtered by the list toolbar ─────────
  // chargeList holds the full pull (all statuses). visibleCharges is what
  // we hand to the table — pure derivation, no fetch on filter change.
  const visibleCharges = useMemo(() => (chargeList || []).filter((c) => {
    if (listFilterProperty && !(c.property && c.property.id === listFilterProperty)) return false;
    if (listFilterStatus && c.status !== listFilterStatus) return false;
    if (listFilterRecipient === 'tenant' && c.payer_type !== 'tenant') return false;
    if (listFilterRecipient === 'unit_owner' && c.payer_type === 'tenant') return false;
    if (listFilterSearch) {
      const q = listFilterSearch.toLowerCase().trim();
      const tenantName = c.tenant
        ? `${c.tenant.first_name || ''} ${c.tenant.last_name || ''} ${c.tenant.email || ''}`
        : '';
      const ownerName = c.owner && c.owner.user
        ? `${c.owner.user.first_name || ''} ${c.owner.user.last_name || ''} ${c.owner.user.email || ''}`
        : '';
      const unitNo = c.tenant && c.tenant.other_details ? c.tenant.other_details.unit_number || '' : '';
      const chargeLabel = c.reference_data
        ? (c.reference_data.info || c.reference_data.payment_code || '') : '';
      const propName = c.property ? c.property.name || '' : '';
      const hay = `${tenantName} ${ownerName} ${unitNo} ${chargeLabel} ${propName}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [chargeList, listFilterProperty, listFilterStatus, listFilterRecipient, listFilterSearch]);

  const clearListFilters = () => {
    setListFilterProperty('');
    setListFilterStatus('');
    setListFilterRecipient('all');
    setListFilterSearch('');
  };

  // Pill-style toggle for "Charge Recipient". Solid green when selected.
  const recipientPillStyle = (active) => ({
    border: '1px solid var(--green_color, #2eb85c)',
    background: active ? 'var(--green_color, #2eb85c)' : '#fff',
    color: active ? '#fff' : 'var(--green_color, #2eb85c)',
    padding: '6px 16px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  // ── Shared card style — applied to both the Create form and the List
  // section so they read as two distinct surfaces stacked on the page.
  const cardStyle = {
    background: '#fff',
    border: '1px solid #ececec',
    borderRadius: 8,
    padding: 18,
    marginBottom: 18,
  };
  const cardHeaderStyle = {
    fontSize: 15,
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: '1px solid #f0f0f0',
  };

  return (
    <Master redirectLink="extracharge" title="Finance" source="Extra Charges">
      {/* ════════════════════ Create New Charge card ════════════════════ */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <i className="fa fa-plus-circle" style={{ marginRight: 8, color: '#27ae60' }} />
          Create New Charge
        </div>
      <div className="row" style={{ marginBottom: 6 }}>
        <div className="col-md-12">
          <label
            className="control-label"
            style={{ marginRight: 12, fontSize: 13 }}
          >
            Charge Recipient
          </label>
          <button
            type="button"
            style={{ ...recipientPillStyle(recipientType === 'tenant'), marginRight: 6 }}
            onClick={() => setRecipientType('tenant')}
          >
            Tenant
          </button>
          <button
            type="button"
            style={recipientPillStyle(recipientType === 'unit_owner')}
            onClick={() => setRecipientType('unit_owner')}
          >
            Unit Owner
          </button>
        </div>
      </div>
      <div className="row">
        <Select
          caption="Property Name"
          value={propertyId}
          onChange={(event) => {
            setPropertyId(event.target.value);
          }}
          columnWidth={3}
          inputRef={propertyRef}
        >
          <option value="">Select property name</option>
          {properties.map((item) => {
            return (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            );
          })}
        </Select>
        {/*
          Both dropdowns are ALWAYS mounted — we just hide the inactive
          one via wrapperStyle. This keeps the select2 binding (created
          at mount) intact across recipient-toggle clicks; otherwise
          unmount/remount would orphan the binding and saveCharges would
          see a stale empty id.
        */}
        <Select
          caption="Tenant"
          value={tenantId}
          onChange={(e) => {
            setTenantId(e.target.value);
          }}
          columnWidth={4}
          className={isSubmitting && !tenantId && recipientType === 'tenant' ? 'error' : ''}
          inputRef={tenantRef}
          wrapperStyle={{ display: recipientType === 'tenant' ? 'block' : 'none' }}
        >
          <option value="">Select tenant</option>
          {tenants.map((item) => {
            return (
              <option key={item.id} value={item.id}>
                {`${item.first_name} ${item.last_name} | ${item.other_details ? item.other_details.unit_number : '--' }`}
              </option>
            );
          })}
        </Select>
        <Select
          caption="Unit Owner"
          value={unitOwnerId}
          onChange={(e) => {
            setUnitOwnerId(e.target.value);
          }}
          columnWidth={4}
          className={isSubmitting && !unitOwnerId && recipientType === 'unit_owner' ? 'error' : ''}
          inputRef={ownerRef}
          wrapperStyle={{ display: recipientType === 'unit_owner' ? 'block' : 'none' }}
        >
          <option value="">Select unit owner</option>
          {unitOwners.map((item) => (
            <option key={item.id} value={item.id}>
              {ownerLabel(item)}
            </option>
          ))}
        </Select>
        <Select
          caption="Extra Charge Name"
          value={chargeId}
          onChange={(e) => {
            setChargeId(e.target.value);
          }}
          columnWidth={3}
          className={isSubmitting && !chargeId ? 'error' : ''}
          inputRef={chargeRef}
        >
          <option value="">Select charge name</option>
          {extraCharges.map((item) => {
            return (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            );
          })}
        </Select>
        <TextInput
          type="number"
          placeholder="Enter amount"
          caption="Amount"
          onChange={(e) => {
            const { value } = e.target;
            // Allow decimals (e.g. 1500.50) and empty string
            if (value === '' || /^\d+(\.\d*)?$/.test(value)) {
              setChargeAmount(value);
            }
          }}
          value={chargeAmount}
          columnWidth={2}
          className={
            (isSubmitting && !chargeAmount) || parseFloat(chargeAmount) <= 0
              ? 'error'
              : undefined
          }
          inputRef={amountRef}
        />
      </div>
      <div className="row">
        <TextInput
          type="text"
          placeholder="Enter description (optional)"
          caption="Description (optional)"
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          value={description}
          columnWidth={7}
        />
        <div className="col-md-3" style={{ marginTop: '30px' }}>
          <CheckBox
            caption="Send Email"
            checked={sendEmail}
            onClick={() => {
              setSendEmail(!sendEmail);
            }}
          />
          <CheckBox
            caption="Send SMS"
            checked={sendSms}
            onClick={() => {
              setSendSms(!sendSms);
            }}
          />
          <CheckBox
            caption="Send WhatsApp"
            checked={sendWhatsapp}
            onClick={() => {
              setSendWhatsapp(!sendWhatsapp);
            }}
          />
        </div>
        <div className="col-md-2 inline-right-button">
          <Button
            caption="Create Charges"
            cssClass="btn-success"
            onClick={saveCharges}
            type="button"
            isBlack={false}
            minWidth={190}
          />
        </div>
      </div>
      </div>{/* ════════════════ end Create New Charge card ═══════════════ */}

      {/* ═══════════════════════ Charges List card ═══════════════════════ */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <i className="fa fa-list-alt" style={{ marginRight: 8, color: '#3498db' }} />
          Charges List
          <span style={{
            fontWeight: 'normal', color: '#7c8896', fontSize: 12, marginLeft: 10,
          }}
          >
            — full history across all properties (filter below to narrow)
          </span>
        </div>

        {/* ── Filter toolbar ───────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
            marginBottom: 14,
            padding: 12,
            background: '#fafbfc',
            border: '1px solid #eef0f2',
            borderRadius: 6,
          }}
        >
          <div style={{ flex: '0 0 220px' }}>
            <label style={{ fontSize: 11, color: '#7c8896', marginBottom: 4, display: 'block' }}>
              Property
            </label>
            <select
              className="form-control"
              style={{ height: 34, borderColor: '#e2e5e9', boxShadow: 'none' }}
              value={listFilterProperty}
              onChange={(e) => setListFilterProperty(e.target.value)}
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <label style={{ fontSize: 11, color: '#7c8896', marginBottom: 4, display: 'block' }}>
              Status
            </label>
            <select
              className="form-control"
              style={{ height: 34, borderColor: '#e2e5e9', boxShadow: 'none' }}
              value={listFilterStatus}
              onChange={(e) => setListFilterStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="fully_paid">Fully Paid</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{ flex: '0 0 150px' }}>
            <label style={{ fontSize: 11, color: '#7c8896', marginBottom: 4, display: 'block' }}>
              Recipient
            </label>
            <select
              className="form-control"
              style={{ height: 34, borderColor: '#e2e5e9', boxShadow: 'none' }}
              value={listFilterRecipient}
              onChange={(e) => setListFilterRecipient(e.target.value)}
            >
              <option value="all">All recipients</option>
              <option value="tenant">Tenants only</option>
              <option value="unit_owner">Unit Owners only</option>
            </select>
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 180 }}>
            <label style={{ fontSize: 11, color: '#7c8896', marginBottom: 4, display: 'block' }}>
              Search
            </label>
            <input
              type="text"
              className="form-control"
              style={{ height: 34, borderColor: '#e2e5e9', boxShadow: 'none' }}
              placeholder="Name, unit, charge or property…"
              value={listFilterSearch}
              onChange={(e) => setListFilterSearch(e.target.value)}
            />
          </div>
          <div>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={clearListFilters}
              title="Clear all filters"
            >
              <i className="fa fa-times" style={{ marginRight: 4 }} />
              Clear
            </button>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: '#7c8896' }}>
            Showing <strong style={{ color: '#2c3e50' }}>{visibleCharges.length}</strong>
            {' '}of {chargeList.length}
          </div>
        </div>

        <ChargesTable
          isLoading={isLoading}
          requestConfig={requestConfig}
          onReload={onReload}
          charges={visibleCharges}
          hasError={hasError}
        />
      </div>{/* ══════════════════ end Charges List card ═══════════════════ */}
    </Master>
  );
};

const mapStateToProps = (state) => {
  return state;
};

ExtraCharge.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

ExtraCharge.defaultProps = {
  user: {},
};

export default connect(mapStateToProps)(ExtraCharge);
